import { operatorRecoveryMessage } from "@hms-operational/operationalRecovery";

/**
 * Extract a user-facing string from an axios/API error.
 *
 * Backend error body shape (GlobalExceptionHandler):
 * { status, path, error, timestamp, correlationId }
 *
 * Billing responses are rewritten to cashier language. Registration
 * fallbacks below stay in place for non-billing calls.
 */
function toApplicationHostWording(message) {
  if (typeof message !== "string" || !message) return message;
  return message
    .replace(/Hospital URL/g, "Application Host")
    .replace(/Application URL/g, "Application Host")
    .replace(/hospital URL/g, "application host")
    .replace(/application URL/g, "application host");
}

function isBillingHttpError(error, data) {
  const path = `${error?.config?.url || ""} ${data && typeof data === "object" ? data.path || "" : ""}`;
  const nested =
    data && typeof data === "object" && data.error && typeof data.error === "object"
      ? data.error.message || ""
      : "";
  const blob = `${typeof data === "string" ? data : ""} ${
    data && typeof data === "object" && typeof data.error === "string" ? data.error : ""
  } ${data && typeof data === "object" && typeof data.message === "string" ? data.message : ""} ${nested}`;
  return /\/billing\b|api\/v1\/billing/i.test(path) || /\bBIL_/.test(blob);
}

export function extractHttpErrorMessage(error, fallbacks = {}) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (isBillingHttpError(error, data)) {
    return operatorRecoveryMessage(error);
  }

  if (typeof data === "string" && data.trim() && !data.trim().startsWith("{") && !data.trim().startsWith("[")) {
    return toApplicationHostWording(data.trim());
  }

  if (data && typeof data === "object") {
    const nestedMessage =
      data.error && typeof data.error === "object" && typeof data.error.message === "string"
        ? data.error.message.trim()
        : "";
    if (typeof data.error === "string" && data.error.trim() && data.error !== "[object Object]") {
      return toApplicationHostWording(data.error.trim());
    }
    if (nestedMessage && nestedMessage !== "[object Object]") {
      return toApplicationHostWording(nestedMessage);
    }
    if (typeof data.message === "string" && data.message.trim() && data.message !== "[object Object]") {
      return toApplicationHostWording(data.message.trim());
    }
  }

  if (status === 400) {
    return fallbacks.badRequest || "Please check your input and try again.";
  }

  if (status === 409) {
    const code =
      (typeof data?.error === "string" && data.error) ||
      (typeof data?.message === "string" && data.message) ||
      "";
    if (code === "SLUG_UNAVAILABLE") {
      return "This application host is not available. Please choose another.";
    }
    if (code === "HOSPITAL_ALREADY_EXISTS") {
      return "Hospital name already exists. Please use a different name.";
    }
    return (
      fallbacks.conflict ||
      "This registration conflicts with an existing record. Please review your details."
    );
  }

  if (status === 500) {
    return fallbacks.serverError || "Something went wrong. Please try again later.";
  }

  if (error?.response) {
    return fallbacks.generic || "An error occurred. Please try again.";
  }

  return fallbacks.network || "Network error occurred. Please check your connection.";
}

/** @deprecated Use extractHttpErrorMessage — kept for existing signup imports */
export function getRegistrationErrorMessage(error) {
  return extractHttpErrorMessage(error);
}
